import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useGetMeuCashbackQuery } from '../../api'
import type { RootState } from '../../store'
import { esquecerCashbackPendente, marcarCashbackPendente, type CashbackPendente, type MotivoDoCashbackPendente } from '../../store/carrinho-slice'
import { cashbackNoPedido, type CashbackNoPedido, type ConsultaDoSaldo, type ValoresDoPedidoNaTela } from './cashback-no-checkout'
import { mensagemDeCashbackAlterado } from './erros-cashback'

interface OndeOCheckoutEsta {
  /** Delivery em loja com `cashbackUsoAtivo` ligado. Fora disso o saldo nem é consultado. */
  lojaAceitaCashback: boolean
  clienteLogado: boolean
  /** Id do cliente logado. Ausente sem login e enquanto o perfil carrega. */
  idUsuario: number | undefined
}

export interface CashbackDoCheckout {
  /** Situação e valores do cashback para o pedido que está na tela. */
  noPedido: (pedido: ValoresDoPedidoNaTela) => CashbackNoPedido
  ligarOuDesligar: (ligado: boolean) => void
  /** CASHBACK_ALTERADO: refaz a consulta do saldo e devolve a frase que conta ao cliente quanto dá para usar agora. */
  atualizarSaldo: (pedido: ValoresDoPedidoNaTela) => Promise<string>
  /** CASHBACK_SEM_CONTA e CASHBACK_USO_DESLIGADO: desliga o switch e refaz a consulta do saldo. */
  desligarEAtualizarSaldo: () => void
  /** Por que há um envio pendente deste cliente, ou null. Com INDISPONIVEL o aviso do passo de enviar fica na tela. */
  motivoDaTentativa: MotivoDoCashbackPendente | null
  /** Envio que falhou e pode ter debitado: guarda o valor que foi com esta chave, para o reenvio sair igual. Vale depois de recarregar. */
  guardarTentativa: (chave: string, valorCashback: number, motivo: MotivoDoCashbackPendente) => void
  /** Qualquer outro desfecho do envio: o débito daquela chave já foi resolvido ou recusado. */
  esquecerTentativa: () => void
}

/**
 * O cashback do checkout do delivery: a consulta do saldo, o switch e a tentativa pendente.
 * Em mesa, comanda, tablet e totem `lojaAceitaCashback` é false, e o hook não consulta nem guarda nada.
 */
export function useCashbackNoCheckout({ lojaAceitaCashback, clienteLogado, idUsuario }: OndeOCheckoutEsta): CashbackDoCheckout {
  const podeConsultar = lojaAceitaCashback && clienteLogado
  const { data: cashback, isError, refetch } = useGetMeuCashbackQuery(undefined, { skip: !podeConsultar })
  const dispatch = useDispatch()
  const pendenteNoCarrinho = useSelector((estado: RootState) => estado.carrinho.envioPedido?.cashbackPendente)
  const pendente = pendenteDoCliente(pendenteNoCarrinho, idUsuario)
  const valorPendente = pendente?.valorCashback ?? null

  // null: o cliente ainda não mexeu no switch. Aí ele nasce ligado só com tentativa pendente, inclusive depois de recarregar.
  const [escolhaDoCliente, setEscolhaDoCliente] = useState<boolean | null>(null)
  const clienteQuerUsar = escolhaDoCliente ?? valorPendente !== null

  function noPedido(pedido: ValoresDoPedidoNaTela): CashbackNoPedido {
    const consulta = { cashback, comErro: isError }
    return cashbackNoPedido({ lojaAceitaCashback, clienteLogado, consulta, clienteQuerUsar, valorPendente, pedido })
  }

  async function consultarSaldoDeNovo(): Promise<ConsultaDoSaldo> {
    // Consulta que não foi feita (`skip`) não tem como ser refeita.
    if (!podeConsultar) return { cashback: undefined, comErro: false }

    const resposta = await refetch()
    return { cashback: resposta.data, comErro: resposta.isError }
  }

  async function atualizarSaldo(pedido: ValoresDoPedidoNaTela): Promise<string> {
    const consulta = await consultarSaldoDeNovo()
    // Quem chamou já esqueceu a tentativa pendente: a conta é só com o saldo novo.
    const comOSaldoNovo = cashbackNoPedido({ lojaAceitaCashback, clienteLogado, consulta, clienteQuerUsar: true, valorPendente: null, pedido })

    // O cliente estava usando: o switch fica ligado com o valor novo, ou desliga se não sobrou nada para usar.
    setEscolhaDoCliente(comOSaldoNovo.maximo > 0)
    return mensagemDeCashbackAlterado(comOSaldoNovo.maximo)
  }

  function desligarEAtualizarSaldo(): void {
    setEscolhaDoCliente(false)
    void consultarSaldoDeNovo()
  }

  function guardarTentativa(chave: string, valorCashback: number, motivo: MotivoDoCashbackPendente): void {
    // Sem cliente identificado não há de quem guardar; o envio com cashback exige login, então isso não acontece.
    if (idUsuario === undefined) return
    dispatch(marcarCashbackPendente({ chave, valorCashback, idUsuario, motivo }))
  }

  function esquecerTentativa(): void {
    dispatch(esquecerCashbackPendente())
  }

  return {
    noPedido,
    ligarOuDesligar: setEscolhaDoCliente,
    atualizarSaldo,
    desligarEAtualizarSaldo,
    motivoDaTentativa: pendente?.motivo ?? null,
    guardarTentativa,
    esquecerTentativa,
  }
}

/** Outro cliente no mesmo aparelho não herda a tentativa: o débito dela foi no saldo de quem a fez. */
function pendenteDoCliente(pendente: CashbackPendente | undefined, idUsuario: number | undefined): CashbackPendente | null {
  if (!pendente) return null
  if (pendente.idUsuario !== idUsuario) return null
  return pendente
}
